import * as React from 'react';

import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import FormEditingHandler from 'Containers/FormEditingHandler';

import RTMSForm from './Form';
import RTMSHistory from './HistoryRowData';
import { useIntl } from 'react-intl';
import { sortPartialDate } from 'neuro-utils';
import DoctorsOrdersReview from './Form/Sessions/components/DoctorsOrdersReview';
import { useAppSelector } from 'Store/index';
import { filterByDocumentType } from 'Utility/documentHandling';

export const RTMSContext = React.createContext<IRTMSContext>({
  fm: (m: string) => m,
  isEditing: false,
  formData: {
    document: {},
    onChange: () => {
      return;
    },
  },
  startEdit: (d: { _id?: string }) => (e: React.MouseEvent<Element, MouseEvent> | undefined) => {
    !!d;
    !!e;
    return;
  },
  documents: [],
  /** Doctors orders with RTMS in current treatment period */
  doctorsOrders: [],
  /** Other doctors orders in current treatment period */
  otherDoctorsOrders: [],
  /** Doctors orders from every treatment period */
  allDoctorsOrders: [],
  ninmtTreatmentPeriods: [],
  contraIndications: [],
  symptomsAndLocations: [],
  doctorsOrdersReview: { active: false, completed: false },
  setDoctorsOrdersReview: () => '',
  anchor: undefined,
  setAnchor: () => '',
  /** For treatment efficiency functionalities */
  patientMySQStatus: undefined,
  /** For treatment efficiency functionalities */
  taskLists: [],
  /** For treatment efficiency functionalities */
  sortedAndMergedMyDocuments: [],
});

interface IRTMSContext {
  fm: (m: string) => string;
  isEditing: boolean;
  formData: IFormData<IDocumentWithTypeV2<'rtms'>>;
  documents: IDocumentWithTypeV2<'rtms'>[];
  doctorsOrders: IDocumentWithTypeV2<'doctorsOrders'>[];
  otherDoctorsOrders: IDocumentWithTypeV2<'doctorsOrders'>[];
  allDoctorsOrders: IDocumentWithTypeV2<'doctorsOrders'>[];
  ninmtTreatmentPeriods: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[];
  contraIndications: IDocumentWithTypeV2<'contraIndicationsToTreatment'>[];
  symptomsAndLocations: IDocumentWithTypeV2<'symptomsAndLocations'>[];
  doctorsOrdersReview: { active: boolean; completed: boolean };
  setDoctorsOrdersReview: React.Dispatch<
    React.SetStateAction<{
      active: boolean;
      completed: boolean;
    }>
  >;
  anchor: string | undefined;
  setAnchor: React.Dispatch<React.SetStateAction<string | undefined>>;
  startEdit: StartEdit;
  patientMySQStatus: string | null | undefined;
  taskLists: ITaskList[];
  sortedAndMergedMyDocuments: IDocumentV2[];
}

const RTMSContent = ({
  editing,
  startEdit,
  formData,
  view,
  fm,
  documents,
  doctorsOrders,
  otherDoctorsOrders,
  allDoctorsOrders,
  ninmtTreatmentPeriods,
  contraIndications,
  symptomsAndLocations,
  doctorsOrdersReview,
  setDoctorsOrdersReview,
  anchor,
  setAnchor,
  patientMySQStatus,
  taskLists,
  sortedAndMergedMyDocuments,
  latestOrderWithRTMS,
}: IRTMSContentProps): React.JSX.Element => {
  const rtmsContext: IRTMSContext = React.useMemo(
    () => ({
      fm,
      isEditing: !!editing && !view?.viewing,
      formData,
      documents,
      doctorsOrders,
      otherDoctorsOrders,
      allDoctorsOrders,
      ninmtTreatmentPeriods,
      contraIndications,
      symptomsAndLocations,
      doctorsOrdersReview,
      setDoctorsOrdersReview,
      anchor,
      setAnchor,
      startEdit,
      patientMySQStatus,
      taskLists,
      sortedAndMergedMyDocuments,
    }),
    [
      fm,
      editing,
      view,
      formData,
      documents,
      doctorsOrders,
      otherDoctorsOrders,
      allDoctorsOrders,
      ninmtTreatmentPeriods,
      contraIndications,
      symptomsAndLocations,
      doctorsOrdersReview,
      setDoctorsOrdersReview,
      anchor,
      setAnchor,
      startEdit,
      patientMySQStatus,
      taskLists,
      sortedAndMergedMyDocuments,
    ],
  );
  return (
    <RTMSContext.Provider value={rtmsContext}>
      <DocumentWrapper>
        <DocumentHeader
          name="rtms"
          headerId={doctorsOrdersReview.active ? 'rtms.doctorsOrdersReview' : 'rtms.title'}
          headerWidth={9}
          infoText={doctorsOrdersReview.active ? 'rtms.uncompletedDoctorsOrderMessage' : undefined}
          editing={editing}
          editButtons={
            <DocumentCreationButton
              name="rtms"
              text="rtms.newRtms"
              onClick={(e: React.MouseEvent): void => {
                startEdit({}, 'rtms')(e);
              }}
              disabled={documents.some(d => !d.hasEnded) || !!latestOrderWithRTMS?.rtms?.endRTMS}
              disabledTooltip={
                latestOrderWithRTMS?.rtms?.endRTMS ? (
                  <span>{fm('rtms.unableToAddNew')}</span>
                ) : (
                  <span>{fm('rtms.endOngoingToAddNew')}</span>
                )
              }
            />
          }
        />
        {editing ? (
          <React.Fragment>
            <div style={{ display: doctorsOrdersReview.active ? 'none' : undefined }}>
              <RTMSForm />
            </div>
            <div style={{ display: doctorsOrdersReview.active ? undefined : 'none' }}>
              <DoctorsOrdersReview />
            </div>
          </React.Fragment>
        ) : null}
        {!editing && !view?.viewing ? <RTMSHistory /> : null}
      </DocumentWrapper>
    </RTMSContext.Provider>
  );
};

const RTMS = ({ documents }: IOwnProps): React.JSX.Element => {
  const patientMySQStatus = useAppSelector((s: IState) => s.myapp.mysqUserId);
  const taskLists = useAppSelector((s: IState) => s.myapp.taskLists);
  const sortedAndMergedMyDocs = useAppSelector((s: IState) => s.myapp.sortedAndMergedDocuments);

  const [doctorsOrdersReview, setDoctorsOrdersReview] = React.useState<{ active: boolean; completed: boolean }>({
    active: false,
    completed: false,
  });

  // Anchor for scrolling to specific sections in form
  const [anchor, setAnchor] = React.useState<string | undefined>(undefined);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const ninmtTreatmentPeriodDocs = filterByDocumentType(documents, 'ninmtTreatmentPeriod');

  let doctorsOrdersDocs: IDocumentWithTypeV2<'doctorsOrders'>[] = [];
  let otherDoctorsOrdersDocs: IDocumentWithTypeV2<'doctorsOrders'>[] = [];
  let allDoctorsOrdersDocs: IDocumentWithTypeV2<'doctorsOrders'>[] = [];
  let contraIndicationsDocs: IDocumentWithTypeV2<'contraIndicationsToTreatment'>[] = [];
  let symptomsAndLocationsDocs: IDocumentWithTypeV2<'symptomsAndLocations'>[] = [];
  let latestOrderWithRTMS: IDocumentWithTypeV2<'doctorsOrders'> | undefined;

  if (Array.isArray(ninmtTreatmentPeriodDocs) && ninmtTreatmentPeriodDocs.length > 0) {
    const latestTreatmentPeriodDate = ninmtTreatmentPeriodDocs.sort((n1, n2) => sortPartialDate(n2.date, n1.date))[0]
      ?.date;

    allDoctorsOrdersDocs = filterByDocumentType(documents, 'doctorsOrders').filter(
      d => Array.isArray(d.treatmentType) && d.treatmentType?.includes('rtms'), // Only doctor's orders with RTMS are relevant
    );

    doctorsOrdersDocs = allDoctorsOrdersDocs.filter(d => sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0);
    latestOrderWithRTMS = doctorsOrdersDocs.toSorted((d1, d2) => sortPartialDate(d2.date, d1.date))[0];

    otherDoctorsOrdersDocs = filterByDocumentType(documents, 'doctorsOrders').filter(
      d =>
        sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0 &&
        (!Array.isArray(d.treatmentType) || d.treatmentType.length === 0) &&
        Object.keys(d).some(k => ['nextInterview', 'ninmtMeeting', 'requestFollowUp'].includes(k)),
    );

    contraIndicationsDocs = filterByDocumentType(documents, 'contraIndicationsToTreatment').filter(
      d => sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0,
    );

    symptomsAndLocationsDocs = filterByDocumentType(documents, 'symptomsAndLocations').filter(
      d => sortPartialDate(d.startDate, latestTreatmentPeriodDate) >= 0,
    );
  }

  const parsedDocs = structuredClone(documents);
  const rtmsDocs = filterByDocumentType(parsedDocs, 'rtms').map(d => {
    if (Array.isArray(d.sessions) && d.sessions.length > 0) {
      d.sessions = d.sessions.filter(s => !(s.removeTS ?? s.removeInfo));
    }
    return d;
  });

  // Track editing status of documents
  const isEditing = rtmsDocs.some(d => d._editing);

  // If not editing any docs, reset doctorsOrdersReview status if active
  React.useEffect(() => {
    if (!isEditing && doctorsOrdersReview.active) {
      setDoctorsOrdersReview({ active: false, completed: false });
    }
  }, [isEditing]);

  // Scroll to top when starting review of doctor's orders
  React.useEffect(() => {
    if (doctorsOrdersReview.active) {
      scrollTo(0, 0);
    } else {
      // Reset anchor when review is done
      setTimeout(() => setAnchor(undefined), 600);
    }
  }, [doctorsOrdersReview.active]);

  const contentProps: Omit<IRTMSContentProps, 'editing' | 'startEdit' | 'formData' | 'view'> = {
    fm,
    documents: rtmsDocs,
    doctorsOrders: doctorsOrdersDocs,
    otherDoctorsOrders: otherDoctorsOrdersDocs,
    allDoctorsOrders: allDoctorsOrdersDocs,
    ninmtTreatmentPeriods: ninmtTreatmentPeriodDocs,
    contraIndications: contraIndicationsDocs,
    symptomsAndLocations: symptomsAndLocationsDocs,
    doctorsOrdersReview,
    setDoctorsOrdersReview,
    anchor,
    setAnchor,
    patientMySQStatus,
    taskLists,
    sortedAndMergedMyDocuments: sortedAndMergedMyDocs,
    latestOrderWithRTMS,
  };

  return (
    <FormEditingHandler
      name="rtms"
      documents={rtmsDocs}
      toolbarProps={{
        saveButtonDisabled: doctorsOrdersReview.active,
        cancelButtonDisabled: doctorsOrdersReview.active,
      }}
    >
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <RTMSContent editing={editing} startEdit={startEdit} formData={formData} view={view} {...contentProps} />
      )}
    </FormEditingHandler>
  );
};

interface IRTMSContentProps extends Omit<IRTMSContext, 'isEditing'> {
  editing: string;
  view?: IView;
  latestOrderWithRTMS: IDocumentWithTypeV2<'doctorsOrders'> | undefined;
}

interface IOwnProps {
  documents: IDocumentWithTypeV2<
    'rtms' | 'doctorsOrders' | 'ninmtTreatmentPeriod' | 'contraIndicationsToTreatment' | 'symptomsAndLocations'
  >[];
}

export default RTMS;
